import "./index.css";
import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
    Timer,
    ArrowLeftRight,
    Target,
    Sparkles,
    Trophy,
    Settings,
    ListTodo,
    Calendar,
    Pause,
    Play,
    Plus,
} from "lucide-react";

import type {
    Task,
    DailyStats,
    TaskDestination,
    ReflectionSettings,
    TaskComment,
} from "@/types";
import { DEFAULT_REFLECTION_SETTINGS } from "@/types";
import {
    isSameDay,
    getElapsedSeconds,
    getDateKey,
    formatTime,
    getRemainingSeconds,
} from "@/utils/helpers";
import {
    alarmManager,
    playCelebrationSound,
    playReflectionChime,
} from "@/utils/audio";
import {
    requestWakeLock,
    releaseWakeLock,
    isWakeLockActive,
} from "@/utils/wakeLock";
import {
    sendTaskOvertimeNotification,
    sendTaskCompleteNotification,
    getNotificationPermission,
} from "@/utils/notifications";

import { ThemeToggle } from "@/components/ThemeToggle";
import { DataTransferModal } from "@/components/DataTransferModal";
import { Confetti } from "@/components/Confetti";
import { Overview } from "@/components/Overview";
import { AddTaskForm } from "@/components/AddTaskForm";
import { ActiveTaskCard } from "@/components/ActiveTaskCard";
import { QueuedTasks } from "@/components/QueuedTasks";
import { CompletedTasks } from "@/components/CompletedTasks";
import { DateSelector } from "@/components/DateSelector";
import { OvertimeDialog } from "@/components/OvertimeDialog";
import { SettingsPanel } from "@/components/SettingsPanel";
import { Backlog } from "@/components/Backlog";
import { CalendarView } from "@/components/CalendarView";
import { Tutorial } from "@/components/Tutorial";
import { ReflectionDialog } from "@/components/ReflectionDialog";

type AppTab = "tasks" | "calendar";

export function App() {
    // Load and save daily stats for days with transferred tasks
    const [dailyStats, setDailyStats] = useState<Record<string, DailyStats>>(
        () => {
            const saved = localStorage.getItem("taskmaster-daily-stats");
            return saved ? JSON.parse(saved) : {};
        }
    );

    const [tasks, setTasks] = useState<Task[]>(() => {
        const saved = localStorage.getItem("taskmaster-tasks");
        if (saved) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const parsedTasks: Task[] = JSON.parse(saved).map((t: any) => {
                const task: Task = {
                    ...t,
                    createdAt: new Date(t.createdAt),
                    completedAt: t.completedAt
                        ? new Date(t.completedAt)
                        : undefined,
                    activeStartedAt: t.activeStartedAt
                        ? new Date(t.activeStartedAt)
                        : undefined,
                    originalDate: t.originalDate
                        ? new Date(t.originalDate)
                        : undefined,
                };

                // If task was active, calculate elapsed time since it was started
                if (task.status === "active" && task.activeStartedAt) {
                    const now = new Date();
                    const additionalSeconds = Math.floor(
                        (now.getTime() - task.activeStartedAt.getTime()) / 1000
                    );
                    task.elapsedSeconds =
                        task.elapsedSeconds + additionalSeconds;
                    task.activeStartedAt = now;
                }

                return task;
            });

            // Group tasks by date to save stats before transferring
            const tasksByDate = new Map<string, Task[]>();
            parsedTasks.forEach((task) => {
                const dateKey = getDateKey(task.createdAt);
                if (!tasksByDate.has(dateKey)) {
                    tasksByDate.set(dateKey, []);
                }
                tasksByDate.get(dateKey)!.push(task);
            });

            // Load existing stats
            const savedStats = localStorage.getItem("taskmaster-daily-stats");
            const stats: Record<string, DailyStats> = savedStats
                ? JSON.parse(savedStats)
                : {};

            // Save stats for past days with incomplete tasks before transferring
            const todayKey = getDateKey(today);
            tasksByDate.forEach((dayTasks, dateKey) => {
                if (dateKey < todayKey) {
                    const hasIncompleteTasks = dayTasks.some(
                        (t) => t.status !== "completed"
                    );
                    // Only save stats if we haven't already (avoid overwriting)
                    if (hasIncompleteTasks && !stats[dateKey]) {
                        stats[dateKey] = {
                            date: dateKey,
                            totalTasks: dayTasks.length,
                            completedTasks: dayTasks.filter(
                                (t) => t.status === "completed"
                            ).length,
                            totalWorkedSeconds: dayTasks.reduce(
                                (sum, t) => sum + t.elapsedSeconds,
                                0
                            ),
                            totalEstimatedMinutes: dayTasks.reduce(
                                (sum, t) => sum + t.estimatedMinutes,
                                0
                            ),
                        };
                    }
                }
            });

            // Save updated stats
            localStorage.setItem(
                "taskmaster-daily-stats",
                JSON.stringify(stats)
            );

            // Now transfer incomplete tasks to today
            return parsedTasks.map((task) => {
                const taskDate = new Date(task.createdAt);
                taskDate.setHours(0, 0, 0, 0);

                if (
                    task.status !== "completed" &&
                    taskDate.getTime() < today.getTime()
                ) {
                    // Preserve the original date if not already set
                    if (!task.originalDate) {
                        task.originalDate = new Date(task.createdAt);
                    }
                    // Move the task to today
                    task.createdAt = new Date();
                }

                return task;
            });
        }
        return [];
    });

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showConfetti, setShowConfetti] = useState(false);
    const [showDataTransfer, setShowDataTransfer] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [overtimeTask, setOvertimeTask] = useState<Task | null>(null);
    const [showOvertimeDialog, setShowOvertimeDialog] = useState(false);
    const [activeTab, setActiveTab] = useState<AppTab>("tasks");
    const [showTutorial, setShowTutorial] = useState(() => {
        const hasSeenTutorial = localStorage.getItem(
            "taskmaster-tutorial-seen"
        );
        return !hasSeenTutorial;
    });
    const [reflectionSettings, setReflectionSettings] =
        useState<ReflectionSettings>(() => {
            const saved = localStorage.getItem(
                "taskmaster-reflection-settings"
            );
            return saved ? JSON.parse(saved) : DEFAULT_REFLECTION_SETTINGS;
        });
    const [showReflectionDialog, setShowReflectionDialog] = useState(false);
    const [reflectionCountdown, setReflectionCountdown] = useState<number>(0); // Seconds until next reflection
    const reflectionTimerRef = useRef<number>(0); // Seconds since last reflection
    const pausedForReflectionTaskId = useRef<string | null>(null); // Track which task was paused for reflection
    const lastPausedTaskId = useRef<string | null>(null); // Track last paused task for header display
    const alarmTriggeredRef = useRef<Set<string>>(new Set());
    const overtimeCheckedRef = useRef(false);
    const notificationSentRef = useRef<Set<string>>(new Set());

    // Restore wake lock if it was enabled
    useEffect(() => {
        const wakeLockSaved = localStorage.getItem("taskmaster-wakelock");
        if (wakeLockSaved === "true") {
            requestWakeLock();
        }
    }, []);

    // Auto-enable wake lock when there's an active task
    useEffect(() => {
        const hasActiveTask = tasks.some((t) => t.status === "active");
        const wakeLockSaved = localStorage.getItem("taskmaster-wakelock");

        if (hasActiveTask && wakeLockSaved === "true" && !isWakeLockActive()) {
            requestWakeLock();
        }
    }, [tasks]);

    // Check for overtime tasks on initial load
    useEffect(() => {
        if (overtimeCheckedRef.current) return;
        overtimeCheckedRef.current = true;

        const activeTask = tasks.find((t) => t.status === "active");
        if (activeTask) {
            const totalElapsed = getElapsedSeconds(activeTask);
            const remaining = activeTask.estimatedMinutes * 60 - totalElapsed;
            if (remaining < 0) {
                setOvertimeTask(activeTask);
                setShowOvertimeDialog(true);
            }
        }
    }, [tasks]);

    // Save to localStorage
    useEffect(() => {
        localStorage.setItem("taskmaster-tasks", JSON.stringify(tasks));
    }, [tasks]);

    // Save daily stats to localStorage
    useEffect(() => {
        localStorage.setItem(
            "taskmaster-daily-stats",
            JSON.stringify(dailyStats)
        );
    }, [dailyStats]);

    // Save reflection settings to localStorage
    useEffect(() => {
        localStorage.setItem(
            "taskmaster-reflection-settings",
            JSON.stringify(reflectionSettings)
        );
    }, [reflectionSettings]);

    // Handle reflection settings being enabled while a task is active
    // Use a ref to track previous enabled state to only react to actual changes
    const prevReflectionEnabled = useRef(reflectionSettings.enabled);
    const prevReflectionInterval = useRef(reflectionSettings.intervalMinutes);
    useEffect(() => {
        const wasEnabled = prevReflectionEnabled.current;
        const isEnabled = reflectionSettings.enabled;
        const prevInterval = prevReflectionInterval.current;
        const newInterval = reflectionSettings.intervalMinutes;

        prevReflectionEnabled.current = isEnabled;
        prevReflectionInterval.current = newInterval;

        // Handle enabling/disabling
        if (wasEnabled !== isEnabled) {
            if (isEnabled) {
                // Just enabled - start fresh
                reflectionTimerRef.current = 0;
                setReflectionCountdown(newInterval * 60);
            } else {
                // Just disabled - clear everything
                reflectionTimerRef.current = 0;
                setReflectionCountdown(0);
            }
        } else if (isEnabled && prevInterval !== newInterval) {
            // Interval changed while enabled - reset with new interval
            reflectionTimerRef.current = 0;
            setReflectionCountdown(newInterval * 60);
        }
    }, [reflectionSettings.enabled, reflectionSettings.intervalMinutes]);

    // Use refs to track state values for use in intervals without causing re-creation
    const showReflectionDialogRef = useRef(showReflectionDialog);
    useEffect(() => {
        showReflectionDialogRef.current = showReflectionDialog;
    }, [showReflectionDialog]);

    const tasksRef = useRef(tasks);
    useEffect(() => {
        tasksRef.current = tasks;
    }, [tasks]);

    // Timer effect for task elapsed time and overtime only
    useEffect(() => {
        const interval = setInterval(() => {
            setTasks((prev) => {
                return prev.map((task) => {
                    if (task.status === "active" && task.activeStartedAt) {
                        const now = new Date();
                        const activeSeconds = Math.floor(
                            (now.getTime() - task.activeStartedAt.getTime()) /
                                1000
                        );
                        const totalElapsed =
                            task.elapsedSeconds + activeSeconds;
                        const remainingSeconds =
                            task.estimatedMinutes * 60 - totalElapsed;

                        if (
                            remainingSeconds <= 0 &&
                            !alarmTriggeredRef.current.has(task.id)
                        ) {
                            alarmTriggeredRef.current.add(task.id);
                            alarmManager.startAlarm(task.id);

                            if (
                                getNotificationPermission() === "granted" &&
                                !notificationSentRef.current.has(task.id)
                            ) {
                                notificationSentRef.current.add(task.id);
                                sendTaskOvertimeNotification(task.name);
                            }
                        }

                        return { ...task };
                    }
                    return task;
                });
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Separate timer effect for reflection countdown only
    useEffect(() => {
        if (!reflectionSettings.enabled) return;

        const reflectionInterval = setInterval(() => {
            const currentTasks = tasksRef.current;
            const activeTask = currentTasks.find((t) => t.status === "active");
            const dialogOpen = showReflectionDialogRef.current;
            const intervalSeconds = reflectionSettings.intervalMinutes * 60;

            if (activeTask && !dialogOpen) {
                reflectionTimerRef.current += 1;
                const remaining = Math.max(
                    0,
                    intervalSeconds - reflectionTimerRef.current
                );
                setReflectionCountdown(remaining);

                if (reflectionTimerRef.current >= intervalSeconds) {
                    // Time for reflection!
                    playReflectionChime();
                    pausedForReflectionTaskId.current = activeTask.id;
                    alarmManager.stopAlarm(activeTask.id);
                    reflectionTimerRef.current = 0;

                    // Pause the task
                    setTasks((prev) =>
                        prev.map((task) => {
                            if (
                                task.id === activeTask.id &&
                                task.activeStartedAt
                            ) {
                                const now = new Date();
                                const activeSeconds = Math.floor(
                                    (now.getTime() -
                                        task.activeStartedAt.getTime()) /
                                        1000
                                );
                                return {
                                    ...task,
                                    status: "pending" as const,
                                    elapsedSeconds:
                                        task.elapsedSeconds + activeSeconds,
                                    activeStartedAt: undefined,
                                };
                            }
                            return task.id === activeTask.id
                                ? {
                                      ...task,
                                      status: "pending" as const,
                                      activeStartedAt: undefined,
                                  }
                                : task;
                        })
                    );

                    setShowReflectionDialog(true);
                }
            } else if (!activeTask) {
                // No active task - reset reflection
                if (reflectionTimerRef.current !== 0) {
                    reflectionTimerRef.current = 0;
                    setReflectionCountdown(0);
                }
            }
        }, 1000);

        return () => clearInterval(reflectionInterval);
    }, [reflectionSettings.enabled, reflectionSettings.intervalMinutes]);

    const addTask = useCallback(
        (
            name: string,
            minutes: number,
            destination: TaskDestination = "today",
            tags: string[] = []
        ) => {
            const newTask: Task = {
                id: crypto.randomUUID(),
                name,
                estimatedMinutes: minutes,
                elapsedSeconds: 0,
                status: "pending",
                createdAt: new Date(),
                isBacklog: destination === "backlog",
                tags: tags.length > 0 ? tags : undefined,
            };
            setTasks((prev) => [...prev, newTask]);
        },
        []
    );

    const moveToToday = useCallback((id: string) => {
        setTasks((prev) =>
            prev.map((task) =>
                task.id === id
                    ? { ...task, isBacklog: false, createdAt: new Date() }
                    : task
            )
        );
    }, []);

    const moveToBacklog = useCallback((id: string) => {
        setTasks((prev) =>
            prev.map((task) =>
                task.id === id ? { ...task, isBacklog: true } : task
            )
        );
    }, []);

    const renameTask = useCallback((id: string, newName: string) => {
        if (!newName.trim()) return;
        setTasks((prev) =>
            prev.map((task) =>
                task.id === id ? { ...task, name: newName.trim() } : task
            )
        );
    }, []);

    const startTask = useCallback(
        (id: string) => {
            // Reset reflection timer when starting a task
            reflectionTimerRef.current = 0;
            // Clear last paused task since we're starting a new one
            lastPausedTaskId.current = null;
            // Immediately update countdown to show full interval if reflections are enabled
            if (reflectionSettings.enabled) {
                setReflectionCountdown(reflectionSettings.intervalMinutes * 60);
            }
            setTasks((prev) =>
                prev.map((task) =>
                    task.id === id
                        ? {
                              ...task,
                              status: "active",
                              activeStartedAt: new Date(),
                          }
                        : task
                )
            );
        },
        [reflectionSettings.enabled, reflectionSettings.intervalMinutes]
    );

    const pauseTask = useCallback((id: string) => {
        alarmManager.stopAlarm(id);
        // Reset reflection timer when pausing
        reflectionTimerRef.current = 0;
        setReflectionCountdown(0);
        // Track last paused task for header quick-resume
        lastPausedTaskId.current = id;
        setTasks((prev) =>
            prev.map((task) => {
                if (task.id === id && task.activeStartedAt) {
                    const now = new Date();
                    const activeSeconds = Math.floor(
                        (now.getTime() - task.activeStartedAt.getTime()) / 1000
                    );
                    return {
                        ...task,
                        status: "pending",
                        elapsedSeconds: task.elapsedSeconds + activeSeconds,
                        activeStartedAt: undefined,
                    };
                }
                return task.id === id
                    ? { ...task, status: "pending", activeStartedAt: undefined }
                    : task;
            })
        );
    }, []);

    const completeTask = useCallback((id: string) => {
        alarmManager.stopAlarm(id);
        alarmTriggeredRef.current.delete(id);
        notificationSentRef.current.delete(id);

        // Clear reflection state if this task was paused for reflection or is active
        if (pausedForReflectionTaskId.current === id) {
            pausedForReflectionTaskId.current = null;
        }
        reflectionTimerRef.current = 0;
        setReflectionCountdown(0);

        let taskName = "";
        setTasks((prev) =>
            prev.map((task) => {
                if (task.id === id) {
                    taskName = task.name;
                    let finalElapsed = task.elapsedSeconds;
                    if (task.activeStartedAt) {
                        const now = new Date();
                        finalElapsed += Math.floor(
                            (now.getTime() - task.activeStartedAt.getTime()) /
                                1000
                        );
                    }
                    return {
                        ...task,
                        status: "completed",
                        completedAt: new Date(),
                        elapsedSeconds: finalElapsed,
                        activeStartedAt: undefined,
                    };
                }
                return task;
            })
        );
        // Play celebration sound and show confetti
        playCelebrationSound();
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 100);

        // Send completion notification
        if (getNotificationPermission() === "granted" && taskName) {
            sendTaskCompleteNotification(taskName);
        }

        // Close overtime dialog if open
        setShowOvertimeDialog(false);
        setOvertimeTask(null);
    }, []);

    const uncompleteTask = useCallback((id: string) => {
        setTasks((prev) =>
            prev.map((task) =>
                task.id === id
                    ? { ...task, status: "pending", completedAt: undefined }
                    : task
            )
        );
    }, []);

    const deleteTask = useCallback((id: string) => {
        alarmManager.stopAlarm(id);
        alarmTriggeredRef.current.delete(id);

        // Clear reflection state if this task was paused for reflection
        if (pausedForReflectionTaskId.current === id) {
            pausedForReflectionTaskId.current = null;
            setShowReflectionDialog(false);
        }

        setTasks((prev) => prev.filter((task) => task.id !== id));
    }, []);

    const addTimeToTask = useCallback((id: string, minutes: number) => {
        setTasks((prev) =>
            prev.map((task) => {
                if (task.id !== id) return task;

                const newEstimatedMinutes = Math.max(
                    1,
                    task.estimatedMinutes + minutes
                );
                const totalElapsed = getElapsedSeconds(task);
                const newRemainingSeconds =
                    newEstimatedMinutes * 60 - totalElapsed;

                if (newRemainingSeconds > 0) {
                    alarmManager.stopAlarm(id);
                    alarmTriggeredRef.current.delete(id);
                }

                return {
                    ...task,
                    estimatedMinutes: newEstimatedMinutes,
                };
            })
        );

        // Close overtime dialog after adding time
        setShowOvertimeDialog(false);
        setOvertimeTask(null);
    }, []);

    const handleOvertimeContinue = useCallback(() => {
        setShowOvertimeDialog(false);
        setOvertimeTask(null);
    }, []);

    const handleOvertimeAddTime = useCallback(
        (minutes: number) => {
            if (overtimeTask) {
                addTimeToTask(overtimeTask.id, minutes);
            }
        },
        [overtimeTask, addTimeToTask]
    );

    const handleOvertimeComplete = useCallback(() => {
        if (overtimeTask) {
            completeTask(overtimeTask.id);
        }
    }, [overtimeTask, completeTask]);

    const handleTutorialComplete = useCallback(() => {
        localStorage.setItem("taskmaster-tutorial-seen", "true");
        setShowTutorial(false);
        // Create the tutorial task
        addTask("Go through tutorial", 5, "today");
    }, [addTask]);

    const handleTutorialSkip = useCallback(() => {
        localStorage.setItem("taskmaster-tutorial-seen", "true");
        setShowTutorial(false);
    }, []);

    // Function to show tutorial again (can be called from settings)
    const showTutorialAgain = useCallback(() => {
        setShowTutorial(true);
    }, []);

    // Comment management functions
    const addComment = useCallback((taskId: string, text: string) => {
        const comment: TaskComment = {
            id: crypto.randomUUID(),
            text,
            timestamp: new Date(),
        };
        setTasks((prev) =>
            prev.map((task) =>
                task.id === taskId
                    ? { ...task, comments: [...(task.comments || []), comment] }
                    : task
            )
        );
    }, []);

    const editComment = useCallback(
        (taskId: string, commentId: string, newText: string) => {
            setTasks((prev) =>
                prev.map((task) =>
                    task.id === taskId
                        ? {
                              ...task,
                              comments: (task.comments || []).map((c) =>
                                  c.id === commentId
                                      ? { ...c, text: newText }
                                      : c
                              ),
                          }
                        : task
                )
            );
        },
        []
    );

    const deleteComment = useCallback((taskId: string, commentId: string) => {
        setTasks((prev) =>
            prev.map((task) =>
                task.id === taskId
                    ? {
                          ...task,
                          comments: (task.comments || []).filter(
                              (c) => c.id !== commentId
                          ),
                      }
                    : task
            )
        );
    }, []);

    // Tag management functions
    const updateTaskTags = useCallback((taskId: string, tags: string[]) => {
        setTasks((prev) =>
            prev.map((task) => (task.id === taskId ? { ...task, tags } : task))
        );
    }, []);

    // Reflection dialog handlers
    const handleReflectionSubmit = useCallback(
        (text: string) => {
            const taskId = pausedForReflectionTaskId.current;
            if (taskId) {
                addComment(taskId, text);
                // Resume the task
                startTask(taskId);
                pausedForReflectionTaskId.current = null;
            }
            setShowReflectionDialog(false);
        },
        [addComment, startTask]
    );

    const handleReflectionSkip = useCallback(() => {
        const taskId = pausedForReflectionTaskId.current;
        if (taskId) {
            // Resume the task without adding a comment
            startTask(taskId);
            pausedForReflectionTaskId.current = null;
        }
        setShowReflectionDialog(false);
    }, [startTask]);

    const isToday = isSameDay(selectedDate, new Date());
    const todaysTasks = tasks.filter(
        (t) => !t.isBacklog && isSameDay(new Date(t.createdAt), selectedDate)
    );
    const backlogTasks = tasks.filter((t) => t.isBacklog);

    // Collect all unique tags from all tasks for autocomplete
    const allTags = Array.from(
        new Set(tasks.flatMap((t) => t.tags || []))
    ).sort();

    const visibleTasks = isToday
        ? todaysTasks
        : todaysTasks.filter((t) => t.status === "completed");

    // Find active task from ALL tasks (not just today's) for the header display
    const activeTask = tasks.find((t) => t.status === "active");
    const hasActiveTask = !!activeTask;
    const pendingTasks = todaysTasks.filter((t) => t.status === "pending");

    // Find last paused task if no active task (must still be pending, not completed)
    const lastPausedTask =
        !activeTask && lastPausedTaskId.current
            ? tasks.find(
                  (t) =>
                      t.id === lastPausedTaskId.current &&
                      t.status === "pending"
              )
            : null;

    // Update browser tab title with active task info
    useEffect(() => {
        if (activeTask) {
            const remaining = getRemainingSeconds(activeTask);
            document.title = `${formatTime(remaining)} - ${
                activeTask.name
            } | TaskMaster`;
        } else {
            document.title = "TaskMaster";
        }
    }, [activeTask, tasks]);
    const completedTasks = visibleTasks.filter((t) => t.status === "completed");

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
            {/* Header */}
            <header className="border-b bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-center justify-between min-h-[4rem] gap-2 py-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg sm:rounded-xl shadow-lg shadow-violet-500/20 shrink-0">
                                <Timer className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            </div>
                            {activeTask ? (
                                <div className="min-w-0 flex items-center gap-2">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`text-base sm:text-xl font-mono font-bold ${
                                                    getRemainingSeconds(
                                                        activeTask
                                                    ) < 0
                                                        ? "text-red-500"
                                                        : "text-green-600 dark:text-green-400"
                                                }`}
                                            >
                                                {formatTime(
                                                    getRemainingSeconds(
                                                        activeTask
                                                    )
                                                )}
                                            </span>
                                            <span className="text-xs text-muted-foreground hidden sm:inline">
                                                {getRemainingSeconds(
                                                    activeTask
                                                ) < 0
                                                    ? "overtime"
                                                    : "remaining"}
                                            </span>
                                        </div>
                                        <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 truncate max-w-[100px] sm:max-w-[200px]">
                                            {activeTask.name}
                                        </p>
                                    </div>
                                    {/* Quick controls */}
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() =>
                                                pauseTask(activeTask.id)
                                            }
                                            title="Pause task"
                                        >
                                            <Pause className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() =>
                                                addTimeToTask(activeTask.id, 5)
                                            }
                                            title="Add 5 minutes"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ) : lastPausedTask ? (
                                <div className="min-w-0 flex items-center gap-2">
                                    <div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-xs text-muted-foreground">
                                                Paused
                                            </span>
                                            <span className="text-base sm:text-lg font-mono font-medium text-amber-600 dark:text-amber-400">
                                                {formatTime(
                                                    lastPausedTask.estimatedMinutes *
                                                        60 -
                                                        lastPausedTask.elapsedSeconds
                                                )}
                                            </span>
                                        </div>
                                        <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 truncate max-w-[100px] sm:max-w-[200px]">
                                            {lastPausedTask.name}
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() =>
                                            startTask(lastPausedTask.id)
                                        }
                                        title="Resume task"
                                    >
                                        <Play className="w-4 h-4" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="min-w-0">
                                    <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white truncate">
                                        TaskMaster
                                    </h1>
                                    <p className="text-xs text-muted-foreground hidden sm:block">
                                        Focus. Track. Achieve.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setShowSettings(true)}
                                className="h-9 w-9"
                                title="Settings"
                            >
                                <Settings className="w-5 h-5 text-slate-500" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setShowDataTransfer(true)}
                                className="h-9 w-9"
                                title="Import / Export Data"
                            >
                                <ArrowLeftRight className="w-5 h-5 text-violet-500" />
                            </Button>
                            <ThemeToggle />
                            {activeTab === "tasks" && (
                                <DateSelector
                                    selectedDate={selectedDate}
                                    onDateChange={setSelectedDate}
                                />
                            )}
                        </div>
                    </div>
                    {/* Tab Navigation */}
                    <div className="flex gap-1 pb-2">
                        <Button
                            variant={
                                activeTab === "tasks" ? "default" : "ghost"
                            }
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => setActiveTab("tasks")}
                        >
                            <ListTodo className="w-4 h-4 mr-1" />
                            Tasks
                        </Button>
                        <Button
                            variant={
                                activeTab === "calendar" ? "default" : "ghost"
                            }
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => setActiveTab("calendar")}
                        >
                            <Calendar className="w-4 h-4 mr-1" />
                            Calendar
                        </Button>
                    </div>
                </div>
            </header>

            {/* Modals */}
            <DataTransferModal
                isOpen={showDataTransfer}
                onClose={() => setShowDataTransfer(false)}
                tasks={tasks}
                dailyStats={dailyStats}
                onImport={(importedTasks, importedStats) => {
                    setTasks(importedTasks);
                    setDailyStats(importedStats);
                }}
            />

            <SettingsPanel
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                onShowTutorial={showTutorialAgain}
                reflectionSettings={reflectionSettings}
                onReflectionSettingsChange={setReflectionSettings}
            />

            <ReflectionDialog
                isOpen={showReflectionDialog}
                taskName={
                    tasks.find(
                        (t) => t.id === pausedForReflectionTaskId.current
                    )?.name || ""
                }
                onSubmit={handleReflectionSubmit}
                onSkip={handleReflectionSkip}
            />

            {overtimeTask && (
                <OvertimeDialog
                    task={overtimeTask}
                    isOpen={showOvertimeDialog}
                    onContinue={handleOvertimeContinue}
                    onAddTime={handleOvertimeAddTime}
                    onComplete={handleOvertimeComplete}
                />
            )}

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {activeTab === "tasks" ? (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Left Column - Main Content */}
                        <div className="lg:col-span-8 space-y-6">
                            {activeTask && isToday && (
                                <ActiveTaskCard
                                    task={activeTask}
                                    onPause={() => pauseTask(activeTask.id)}
                                    onComplete={() =>
                                        completeTask(activeTask.id)
                                    }
                                    onDelete={() => deleteTask(activeTask.id)}
                                    onAddTime={(mins) =>
                                        addTimeToTask(activeTask.id, mins)
                                    }
                                    onEditComment={(commentId, text) =>
                                        editComment(
                                            activeTask.id,
                                            commentId,
                                            text
                                        )
                                    }
                                    onDeleteComment={(commentId) =>
                                        deleteComment(activeTask.id, commentId)
                                    }
                                    onUpdateTags={(tags) =>
                                        updateTaskTags(activeTask.id, tags)
                                    }
                                    onAddComment={(text) =>
                                        addComment(activeTask.id, text)
                                    }
                                    reflectionCountdown={reflectionCountdown}
                                    reflectionEnabled={
                                        reflectionSettings.enabled
                                    }
                                    allTags={allTags}
                                />
                            )}

                            {isToday && (
                                <AddTaskForm
                                    onAdd={addTask}
                                    allTags={allTags}
                                />
                            )}

                            {isToday && pendingTasks.length > 0 && (
                                <QueuedTasks
                                    tasks={todaysTasks}
                                    onStart={startTask}
                                    onDelete={deleteTask}
                                    onAddTime={addTimeToTask}
                                    onMoveToBacklog={moveToBacklog}
                                    onRename={renameTask}
                                    hasActiveTask={hasActiveTask}
                                    onEditComment={editComment}
                                    onDeleteComment={deleteComment}
                                    onUpdateTags={updateTaskTags}
                                    onAddComment={addComment}
                                    allTags={allTags}
                                />
                            )}

                            {isToday &&
                                !activeTask &&
                                pendingTasks.length === 0 && (
                                    <Card className="border-dashed border-2">
                                        <CardContent className="py-16 text-center">
                                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-violet-100 to-purple-100 dark:from-violet-900/30 dark:to-purple-900/30 flex items-center justify-center">
                                                <Target className="w-10 h-10 text-violet-500" />
                                            </div>
                                            <h3 className="text-lg font-semibold mb-2">
                                                Ready to be productive?
                                            </h3>
                                            <p className="text-muted-foreground max-w-sm mx-auto">
                                                Add your first task above and
                                                start tracking your time to stay
                                                focused and achieve more.
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}

                            {/* Backlog Section */}
                            {isToday && backlogTasks.length > 0 && (
                                <Backlog
                                    tasks={tasks}
                                    onMoveToToday={moveToToday}
                                    onDelete={deleteTask}
                                    onRename={renameTask}
                                    hasActiveTask={hasActiveTask}
                                    onEditComment={editComment}
                                    onDeleteComment={deleteComment}
                                    onUpdateTags={updateTaskTags}
                                    onAddComment={addComment}
                                    existingTags={allTags}
                                />
                            )}

                            {completedTasks.length > 0 && (
                                <CompletedTasks
                                    tasks={visibleTasks}
                                    onUndo={uncompleteTask}
                                    isToday={isToday}
                                    onEditComment={editComment}
                                    onDeleteComment={deleteComment}
                                    onAddComment={addComment}
                                />
                            )}
                        </div>

                        {/* Right Column - Stats Sidebar */}
                        <div className="lg:col-span-4 space-y-6">
                            <div className="lg:sticky lg:top-24">
                                <Overview
                                    tasks={tasks}
                                    selectedDate={selectedDate}
                                    dailyStats={dailyStats}
                                />

                                <Card className="mt-6 border-0 shadow-lg">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Sparkles className="w-4 h-4 text-yellow-500" />
                                            Quick Stats
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Tasks Today
                                            </span>
                                            <Badge variant="secondary">
                                                {todaysTasks.length}
                                            </Badge>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                In Progress
                                            </span>
                                            <Badge
                                                variant={
                                                    activeTask
                                                        ? "default"
                                                        : "secondary"
                                                }
                                            >
                                                {activeTask ? 1 : 0}
                                            </Badge>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Waiting
                                            </span>
                                            <Badge variant="secondary">
                                                {pendingTasks.length}
                                            </Badge>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Completed
                                            </span>
                                            <Badge variant="success">
                                                {completedTasks.length}
                                            </Badge>
                                        </div>
                                    </CardContent>
                                </Card>

                                {completedTasks.length >= 3 && (
                                    <Card className="mt-6 border-0 bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/20">
                                        <CardContent className="py-6 text-center">
                                            <Trophy className="w-12 h-12 mx-auto mb-3 text-yellow-300" />
                                            <h3 className="font-semibold text-lg">
                                                Great Progress!
                                            </h3>
                                            <p className="text-sm text-white/80 mt-1">
                                                You've completed{" "}
                                                {completedTasks.length} tasks
                                                today. Keep it up!
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Calendar Tab */
                    <div className="max-w-2xl mx-auto">
                        <CalendarView
                            tasks={tasks}
                            dailyStats={dailyStats}
                            onSelectDate={(date) => {
                                setSelectedDate(date);
                                setActiveTab("tasks");
                            }}
                        />
                    </div>
                )}
            </main>

            <Confetti show={showConfetti} />

            {showTutorial && (
                <Tutorial
                    onComplete={handleTutorialComplete}
                    onSkip={handleTutorialSkip}
                />
            )}
        </div>
    );
}

export default App;
